import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { todayISO } from '../utils/dates';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

const today = todayISO;

/** The tax year a date falls in, by its first calendar year (2026 for April 2026 to March 2027), and its quarter. */
const taxQuarter = (d) => {
  const month = d.getMonth() + 1;
  const year = month >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  const quarter = month >= 4 ? Math.ceil((month - 3) / 3) : 4;
  return { year, quarter };
};

/**
 * TDS kept back from vendor payments: what is owed to the government and by when, recording a deposit (challan), the
 * quarter's details for the TDS return, and the rates. Shown only when the organization deducts TDS.
 */
function Tds({ active, currency = 'INR', canManage, onChanged }) {
  const [data, setData] = useState(null);
  const [sections, setSections] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [deposit, setDeposit] = useState({ depositDate: today(), challanNumber: '', bsrCode: '', paidFromLedgerId: '' });
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() - 3, 1); // the quarter just ended is the one usually filed
    return taxQuarter(last);
  });
  const [editing, setEditing] = useState(null);
  const [showRates, setShowRates] = useState(false);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : undefined, { style: 'currency', currency, maximumFractionDigits: 2 });
    return (v) => f.format(Number(v || 0));
  }, [currency]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s, l] = await Promise.all([axios.get('/api/tds/overview'), axios.get('/api/tds/settings'), axios.get('/api/accounts/ledgers')]);
      setData(o.data);
      setSections(s.data.sections || []);
      setAccounts((l.data || []).filter((x) => (x.bankAccount != null || x.cashInHand != null) && x.isActive !== false));
      setChosen([]);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not load TDS'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (active) load(); }, [active, load]);

  if (loading && !data) return <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>;
  if (!data) return null;

  const pending = data.pending || [];
  const now = today();
  const chosenTotal = pending.filter((p) => chosen.includes(p.paymentId)).reduce((sum, p) => sum + Number(p.tds), 0);

  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const chooseMonth = (month) => setChosen(pending.filter((p) => p.paymentDate.slice(0, 7) === month).map((p) => p.paymentId));
  const months = [...new Set(pending.map((p) => p.paymentDate.slice(0, 7)))];

  const recordDeposit = async () => {
    if (!deposit.paidFromLedgerId) { toast.warn('Choose the bank or cash account it was paid from'); return; }
    setBusy('deposit');
    try {
      const { data: fresh } = await axios.post('/api/tds/deposits', {
        ...deposit, paidFromLedgerId: Number(deposit.paidFromLedgerId), paymentIds: chosen
      });
      setData(fresh);
      setChosen([]);
      setDeposit((d) => ({ ...d, challanNumber: '', bsrCode: '' }));
      toast.success(`Deposit of ${money(chosenTotal)} recorded`);
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not record the deposit'));
    } finally {
      setBusy(null);
    }
  };

  const undo = async (d) => {
    if (!window.confirm(`Undo the deposit of ${money(d.amount)} on ${formatDate(d.depositDate)}? Its TDS will show as owed again.`)) return;
    setBusy(`undo-${d.id}`);
    try {
      const { data: fresh } = await axios.post(`/api/tds/deposits/${d.id}/undo`);
      setData(fresh);
      toast.success('Deposit undone');
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not undo the deposit'));
    } finally {
      setBusy(null);
    }
  };

  const download = async () => {
    setBusy('export');
    try {
      const response = await axios.get('/api/tds/return', { params: period, responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tds-${period.year}-${String((period.year + 1) % 100).padStart(2, '0')}-q${period.quarter}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Could not download the TDS details');
    } finally {
      setBusy(null);
    }
  };

  const saveSection = async () => {
    setBusy('rates');
    try {
      const body = {};
      ['rateIndividual', 'rateOther', 'rateNoPan', 'singleLimit', 'annualLimit'].forEach((k) => {
        body[k] = editing[k] === '' || editing[k] == null ? null : Number(editing[k]);
      });
      body.isActive = editing.isActive;
      const { data: saved } = await axios.put(`/api/tds/sections/${editing.code}`, body);
      setSections((all) => all.map((s) => (s.code === saved.code ? saved : s)));
      setEditing(null);
      toast.success('Rates saved');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save the rates'));
    } finally {
      setBusy(null);
    }
  };

  const years = [];
  for (let y = taxQuarter(new Date()).year; y >= taxQuarter(new Date()).year - 3; y -= 1) years.push(y);

  return (
    <div className="mt-3">
      <p className="text-muted">
        TDS kept back when paying vendors is owed to the government until you deposit it: by the 7th of the next month (30 April for
        March). Record each deposit here with its challan, and download each quarter&apos;s details for your TDS return.
      </p>

      {!data.tan && (
        <Alert variant="warning" className="small py-2">
          Your TAN isn&apos;t set. It goes on every TDS challan and return; the Org Admin can add it in the organization profile.
        </Alert>
      )}

      <Row className="g-3 mb-3">
        <Col md={4}><Summary label="Owed to the government" value={money(data.owed)} /></Col>
        <Col md={4}>
          <Summary label="Past its due date" value={money(data.overdue)} tone={Number(data.overdue) > 0 ? 'text-danger' : undefined}
            hint={Number(data.overdue) > 0 ? 'Late deposits carry interest; deposit it soon.' : undefined} />
        </Col>
        <Col md={4}><Summary label="TAN" value={data.tan || 'Not set'} /></Col>
      </Row>

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 className="mb-0">To deposit</h6>
          {months.length > 1 && (
            <div className="small">
              Choose a month:{' '}
              {months.map((m) => (
                <Button key={m} size="sm" variant="link" className="p-0 me-2" onClick={() => chooseMonth(m)}>{m}</Button>
              ))}
            </div>
          )}
        </Card.Header>
        <Card.Body>
          {pending.length === 0 ? (
            <div className="text-muted small">Nothing owed. TDS kept back from vendor payments appears here until it is deposited.</div>
          ) : (
            <>
              <Table size="sm" bordered hover responsive className="small">
                <thead>
                  <tr>
                    <th style={{ width: '2rem' }}>
                      <Form.Check checked={chosen.length === pending.length}
                        onChange={(e) => setChosen(e.target.checked ? pending.map((p) => p.paymentId) : [])} />
                    </th>
                    <th>Payment</th><th>Vendor</th><th>Section</th><th className="text-end">Paid</th><th className="text-end">TDS</th><th>Due by</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((p) => (
                    <tr key={p.paymentId}>
                      <td><Form.Check checked={chosen.includes(p.paymentId)} onChange={() => toggle(p.paymentId)} /></td>
                      <td>{p.paymentNumber}<div className="text-muted">{formatDate(p.paymentDate)}</div></td>
                      <td>{p.vendor}<div className="text-muted">{p.pan || 'No PAN'}</div></td>
                      <td>{p.sectionLabel}<div className="text-muted">{Number(p.rate)}%</div></td>
                      <td className="text-end">{money(p.amount)}</td>
                      <td className="text-end">{money(p.tds)}</td>
                      <td>{p.dueDate < now ? <Badge bg="danger">{formatDate(p.dueDate)}</Badge> : formatDate(p.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {chosen.length > 0 && (
                <Row className="g-2 align-items-end">
                  <Col md={2}>
                    <Form.Label className="small mb-1">Deposited on</Form.Label>
                    <DateInput size="sm" max={now} value={deposit.depositDate}
                      onChange={(e) => setDeposit({ ...deposit, depositDate: e.target.value })} />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small mb-1">Paid from</Form.Label>
                    <Form.Select size="sm" value={deposit.paidFromLedgerId} onChange={(e) => setDeposit({ ...deposit, paidFromLedgerId: e.target.value })}>
                      <option value="">Choose...</option>
                      {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small mb-1">Challan no.</Form.Label>
                    <Form.Control size="sm" maxLength={30} value={deposit.challanNumber} onChange={(e) => setDeposit({ ...deposit, challanNumber: e.target.value })} />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small mb-1">BSR code</Form.Label>
                    <Form.Control size="sm" maxLength={10} value={deposit.bsrCode} onChange={(e) => setDeposit({ ...deposit, bsrCode: e.target.value })} />
                  </Col>
                  <Col xs="auto">
                    <Button size="sm" disabled={busy !== null} onClick={recordDeposit}>
                      {busy === 'deposit' ? 'Saving...' : `Record deposit of ${money(chosenTotal)}`}
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header><h6 className="mb-0">Quarterly return</h6></Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col xs="auto">
              <Form.Label className="small mb-1">Tax year</Form.Label>
              <Form.Select size="sm" value={period.year} onChange={(e) => setPeriod({ ...period, year: Number(e.target.value) })}>
                {years.map((y) => <option key={y} value={y}>{y}-{String((y + 1) % 100).padStart(2, '0')}</option>)}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label className="small mb-1">Quarter</Form.Label>
              <Form.Select size="sm" value={period.quarter} onChange={(e) => setPeriod({ ...period, quarter: Number(e.target.value) })}>
                <option value={1}>Q1 (April to June)</option>
                <option value={2}>Q2 (July to September)</option>
                <option value={3}>Q3 (October to December)</option>
                <option value={4}>Q4 (January to March)</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button size="sm" variant="outline-primary" disabled={busy !== null} onClick={download}>
                {busy === 'export' ? 'Preparing...' : 'Download details (CSV)'}
              </Button>
            </Col>
            <Col className="small text-muted">
              Each deduction with the vendor&apos;s PAN, section, amounts and challan: what your CA or TDS software needs for the quarterly
              return (Form 140, formerly 26Q). Due by 31 July, 31 October, 31 January and 31 May.
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {(data.deposits || []).length > 0 && (
        <Card className="mb-3">
          <Card.Header><h6 className="mb-0">Deposits</h6></Card.Header>
          <Card.Body>
            <Table size="sm" bordered hover responsive className="small mb-0">
              <thead>
                <tr><th>Date</th><th>Challan</th><th>BSR code</th><th>From</th><th className="text-end">Amount</th><th>Payments</th><th /></tr>
              </thead>
              <tbody>
                {data.deposits.map((d) => (
                  <tr key={d.id} className={d.status === 'ACTIVE' ? '' : 'text-muted'}>
                    <td>{formatDate(d.depositDate)}</td>
                    <td>{d.challanNumber || '-'}</td>
                    <td>{d.bsrCode || '-'}</td>
                    <td>{d.paidFrom}</td>
                    <td className="text-end">{money(d.amount)}</td>
                    <td>{d.status === 'ACTIVE' ? d.payments : <Badge bg="secondary">Undone</Badge>}</td>
                    <td className="text-end">
                      {canManage && d.status === 'ACTIVE' && (
                        <Button size="sm" variant="outline-danger" disabled={busy !== null} onClick={() => undo(d)}>Undo</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Rates and limits</h6>
          <Button size="sm" variant="link" className="p-0" onClick={() => setShowRates(!showRates)}>{showRates ? 'Hide' : 'Show'}</Button>
        </Card.Header>
        {showRates && (
          <Card.Body>
            <p className="small text-muted">
              Set for tax year 2026-27 under the Income-tax Act, 2025 (the old section numbers are shown, as most people still use them).
              If the law changes{canManage ? ', change them here' : ', your Finance Manager can change them'}. A vendor&apos;s section is set on the vendor.
            </p>
            <Table size="sm" bordered responsive className="small mb-0">
              <thead>
                <tr>
                  <th>Payment for</th><th className="text-end">Individual / HUF</th><th className="text-end">Others</th><th className="text-end">No PAN</th>
                  <th className="text-end">Single payment above</th><th className="text-end">Year&apos;s payments above</th><th />
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => (editing?.code === s.code ? (
                  <tr key={s.code}>
                    <td>{s.label}<Form.Check type="switch" id={`tds-active-${s.code}`} label="In use" checked={editing.isActive}
                      onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} /></td>
                    {['rateIndividual', 'rateOther', 'rateNoPan', 'singleLimit', 'annualLimit'].map((k) => (
                      <td key={k}><Form.Control size="sm" type="number" min="0" step="any" value={editing[k] ?? ''}
                        onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} /></td>
                    ))}
                    <td className="text-nowrap">
                      <Button size="sm" disabled={busy !== null} onClick={saveSection}>Save</Button>{' '}
                      <Button size="sm" variant="link" onClick={() => setEditing(null)}>Cancel</Button>
                    </td>
                  </tr>
                ) : (
                  <tr key={s.code} className={s.isActive ? '' : 'text-muted'}>
                    <td>{s.label}{s.onExcessOnly && <div className="text-muted">On the part above the year&apos;s limit</div>}{!s.isActive && ' (not in use)'}</td>
                    <td className="text-end">{Number(s.rateIndividual)}%</td>
                    <td className="text-end">{Number(s.rateOther)}%</td>
                    <td className="text-end">{Number(s.rateNoPan)}%</td>
                    <td className="text-end">{s.singleLimit ? money(s.singleLimit) : '-'}</td>
                    <td className="text-end">{s.annualLimit ? money(s.annualLimit) : '-'}</td>
                    <td>{canManage && <Button size="sm" variant="link" className="p-0" onClick={() => setEditing({ ...s })}>Change</Button>}</td>
                  </tr>
                )))}
              </tbody>
            </Table>
          </Card.Body>
        )}
      </Card>
    </div>
  );
}

function Summary({ label, value, hint, tone }) {
  return (
    <Card className="h-100">
      <Card.Body className="py-2">
        <div className="small text-muted">{label}</div>
        <div className={`fs-5 fw-semibold ${tone || ''}`}>{value}</div>
        {hint && <div className="small text-muted">{hint}</div>}
      </Card.Body>
    </Card>
  );
}

export default Tds;
